import { Alert, Button, Loader, Stack, Text, TextInput, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Check, ImageOff, Link2 } from "lucide-react";
import { useEffect, useState } from "react";

import { isSessionExpired } from "../../api/errors";
import { useGetFirstAdTiles } from "../../api/generated/endpoints/generation/generation";
import { accessScopeQuery } from "../../api/session";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { firstAdChoice, firstAdReturn, keepFirstAdChoice, productLink } from "../../platform/first-ad-choice";
import { captureAttribution, recordFirstAdStep } from "../../platform/onboarding-tracking";
import { Logo } from "../../ui/Logo";
import classes from "./free-ad.module.css";

/** The owners' entryA mock; every tile and its media come from the same catalog as the app. */
export function FreeAdLanding() {
  useEffect(() => { captureAttribution(); void recordFirstAdStep("entry", {}); }, []);
  const search = useSearch({ from: "/start/free" });
  const choice = firstAdChoice(search);
  const [link, setLink] = useState(choice?.product ?? "");
  const [manual, setManual] = useState(choice?.manual ?? false);
  const [invalid, setInvalid] = useState(false);
  const [sessionError, setSessionError] = useState<unknown>(null);
  const [choosing, setChoosing] = useState(false);
  const tiles = useGetFirstAdTiles();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const choose = async (template: string) => {
    if (choosing) return;
    const product = manual ? "" : productLink(link);
    setInvalid(product === null);
    if (product === null) return;
    const picked = { template, product, manual };
    setSessionError(null);
    setChoosing(true);
    let signedIn = false;
    try {
      await queryClient.fetchQuery({ ...accessScopeQuery(), staleTime: 0 });
      signedIn = true;
    } catch (error) {
      if (!isSessionExpired(error)) {
        setSessionError(error);
        setChoosing(false);
        return;
      }
      // A prior signed-in value must not make signup's guard redirect this expired session.
      queryClient.removeQueries({ queryKey: accessScopeQuery().queryKey, exact: true });
    }
    if (product) void recordFirstAdStep("link_pasted", { template });
    void recordFirstAdStep("template_picked", { template });
    keepFirstAdChoice(picked);
    if (signedIn) void navigate({ href: firstAdReturn(picked) });
    else void navigate({ to: "/sign-up" });
  };
  return (
    <div className={classes.page}>
      <header className={classes.header}><Logo /><Link to="/sign-in">Log in</Link></header>
      <main id="main" className={classes.main}>
        <Title order={1} className={classes.headline}>Make your first ad free</Title>
        <Text className={classes.subtitle}>Paste your product link, then tap a style to start.</Text>
        {tiles.isError ? <ErrorAlert error={tiles.error} /> : null}
        {sessionError === null ? null : <ErrorAlert error={sessionError} />}
        {tiles.isPending ? <Loader aria-label="Loading ad styles" /> : null}
        {tiles.data?.enabled === false ? (
          <Alert>This page is not available here. <Link to="/sign-in">Log in to Created.ai</Link></Alert>
        ) : tiles.data ? (
          <Stack gap="lg" className={classes.entry}>
            {choice && tiles.data.items.some((tile) => tile.id === choice.template) ? (
              <Text component="output" size="sm"><Check size={14} aria-hidden /> Your product and style are saved.</Text>
            ) : null}
            <TextInput label="Product link" type="url" size="lg" leftSection={<Link2 size={20} />}
              placeholder="https://yourbrand.com/products/your-product" value={link} disabled={manual}
              onChange={(event) => { setLink(event.currentTarget.value); setInvalid(false); }}
              error={invalid ? "Paste a valid product link, or add your product by hand." : undefined} />
            <Button variant="subtle" className={classes.manual} classNames={{ label: classes.manualLabel }} onClick={() => { setManual(!manual); setInvalid(false); }}>
              {manual ? "Use a product link instead" : "No Shopify or Amazon link? Add your product by hand"}
            </Button>
            {manual ? <Text size="sm">Choose a style. You can add your product details after sign-up.</Text> : null}
            <div className={classes.tiles}>
              {tiles.data.items.map((tile, index) => (
                <button key={tile.id} type="button" className={classes.tile} disabled={choosing} onClick={() => { void choose(tile.id); }}
                  aria-label={`Choose ${tile.title}`} aria-pressed={choice?.template === tile.id}>
                  {tile.poster_url ? <img src={tile.poster_url} alt="" width={240} height={320}
                    loading={index < 2 ? "eager" : "lazy"} decoding="async" fetchPriority={index === 0 ? "high" : "auto"} /> : <ImageOff aria-hidden />}
                  <span className={classes.badge}>Free trial</span>
                  <span className={classes.name}>{tile.title}</span>
                </button>
              ))}
            </div>
          </Stack>
        ) : null}
      </main>
    </div>
  );
}
